import { api } from "./client";
import type { InvoiceIssuingMode } from "./companies";

export interface InvoiceCustomerPayload {
  eik: string;
  vat_number?: string | null;
  company_name: string;
  city: string;
  address: string;
  mol?: string | null;
}

/** `item` — продукт/ръчен ред; `tip` — записаният бакшиш на сметка(и): без ДДС
 *  (група А, `vat_rate` 0) или облагаем със ставката на групата си (Б 20 % /
 *  В 9 %). BE го проверява срещу сметките в `invoices/tip_lines.py`.
 *  `deposit` — „Приспаднато авансово плащане" на сметка, платена с депозит:
 *  ЕДИНСТВЕНИЯТ ред с отрицателна сума (= −`deposit_amount` на сметката от
 *  `source_bill_ids`, BE го проверява); дължимото по фактурата е остатъкът. */
export type InvoiceLineKind = "item" | "tip" | "deposit";

export interface InvoiceLinePayload {
  name: string;
  unit: string;
  vat_rate: number;
  quantity: number;
  // Редовете за бакшиш и за депозит пращат сумата като низ „x.xx" (точно
  // центовете от BE; депозитът — с минус); продуктовите редове — числа, както досега.
  unit_price: number | string;
  total: number | string;
  // САМО при реда за бакшиш и за депозит (без тях BE приема „item") —
  // продуктовият ред носи точно досегашните шест ключа.
  line_kind?: InvoiceLineKind;
  source_bill_ids?: number[];
}

export interface InvoiceCreatePayload {
  invoice_number: string;
  invoice_type: "tax" | "simplified";
  issue_date: string; // YYYY-MM-DD
  supply_date: string;
  supply_place?: string | null;
  payment_method_id?: number | null;
  customer: InvoiceCustomerPayload;
  received_by?: string | null;
  received_at?: string | null;
  note?: string | null;
  bill_ids: number[];
  lines: InvoiceLinePayload[];
  /** Фактура за депозит („Депозити" → „Фактуриране"): `bill_ids: []`, без
   *  нов бон — BE я връзва с бона на депозита (Бон 1) и записва номера му в
   *  бележката; депозит без бон → 400 (BE `ensure_deposit_invoiceable`). */
  deposit_id?: number;
}

export interface InvoiceLine {
  id: number;
  sort_index: number;
  name: string;
  unit: string;
  vat_rate: number;
  quantity: number;
  unit_price: number;
  total: number;
  // Optional срещу по-старо BE / стари фактури (= „item").
  line_kind?: InvoiceLineKind;
  // `voluntary_tip_no_vat` | `taxable` за реда за бакшиш, иначе null — пише го само BE.
  tax_treatment?: string | null;
  source_bill_ids?: number[] | null;
}

/** Откъде е документът (BE го извежда от първия ред на бележката — без
 *  колона): `fiscal` — фискална фактура или кредитно известие, издадени от
 *  фискалното устройство при закриването; `software` — всички останали. */
export type InvoiceSource = "software" | "fiscal";

export interface Invoice {
  id: number;
  invoice_number: string;
  /** `"tax"` | `"simplified"` | `"credit_note"` (кредитното известие към
   *  фискална фактура — сумите и редовете му са отрицателни). */
  invoice_type: string;
  // Фискалната фактура (BE `fiscal_invoice_ref`) — optional срещу по-старо BE,
  // където всяка фактура е софтуерна.
  source?: InvoiceSource;
  /** Номерът на фискалния бон, който Е фактурата (или кредитното известие). */
  fiscal_receipt_number?: string | null;
  fiscal_device_serial?: string | null;
  /** Кредитно известие — номерът на фактурата, към която е. */
  credit_note_of?: string | null;
  /** Запазеното място на неизползван номер от общата поредица — не е
   *  документ (анулиран ред с нулеви суми); справката го пише така. */
  is_unused_number?: boolean;
  /** Фискалните бонове на документа — „Бон №“ в справката. */
  receipt_numbers?: string[];
  /** Само в списъка: кредитното известие, което неутрализира тази фискална
   *  фактура (тя остава жива); null — няма. */
  credited_by?: string | null;
  /** Свободна фактура (ръчни редове, без сметка и не за депозит) — „Свободна
   *  фактура“ във „Вид“ на справката (BE `free_invoice_ids`, 0.0.391);
   *  optional срещу по-старо BE. */
  is_free?: boolean;
  issue_date: string;
  supply_date: string;
  supply_place: string | null;
  payment_method_id: number | null;

  company_id: number;
  supplier_name: string;
  supplier_eik: string;
  supplier_vat: string | null;
  supplier_address: string | null;
  supplier_mol: string | null;
  supplier_bank_name: string | null;
  supplier_bic: string | null;
  supplier_iban: string | null;

  customer_id: number;
  customer_name: string;
  customer_eik: string;
  customer_vat: string | null;
  customer_city: string;
  customer_address: string;
  customer_mol: string | null;

  received_by: string | null;
  received_at: string | null;
  note: string | null;

  subtotal_taxable: number;
  subtotal_non_taxable: number;
  vat_total: number;
  total_sum: number;
  grand_total: number;
  // Редовете за бакшиш БЕЗ ДДС — извън ДДС групите и `total_sum`, вътре в
  // `grand_total` (облагаемият бакшиш е в ДДС групата си, не тук). Decimal →
  // низ; "0.00"/липсва без такъв бакшиш.
  tip_total?: string;

  created_at: string;
  created_by_user_id: number;
  created_by_name: string | null;

  cancelled_at: string | null;
  cancelled_by_user_id: number | null;
  // Първият печат на ОРИГИНАЛА (A4 или POS); null — още не е отпечатан и
  // справката печата първо него, после — само дубликати.
  original_printed_at?: string | null;

  lines: InvoiceLine[];
  bill_ids: number[];
}

export const createInvoice = async (
  data: InvoiceCreatePayload,
): Promise<Invoice> => {
  const response = await api.post<Invoice>("/invoices", data);
  return response.data;
};

/** Правото „Свободна фактура" (org seed, „Маркиране") — заедно с `invoicing`;
 *  без него редът в „Фактуриране" го няма, а BE отказва с 403 (0.0.391). */
export const FREE_INVOICE_PERMISSION = "free_invoice";

/** „Фактуриране → Свободна фактура": без сметки, само „По сметка" и без бон
 *  (BE `free_invoice.py`, 0.0.389 — друг метод е 400). */
export interface FreeInvoicePayload extends Omit<InvoiceCreatePayload, "deposit_id"> {
  payment_method_id: number;
  // Без `deposit_id`: фактурата за депозит е `POST /invoices` (BE отказва с 400).
}

/** 201 с издадената фактура; повторение със загубен отговор — вече издадената. */
export const createFreeInvoice = async (data: FreeInvoicePayload): Promise<Invoice> => {
  const response = await api.post<Invoice>("/invoices/free", data);
  return response.data;
};

export interface InvoiceListFilters {
  date_from?: string; // YYYY-MM-DD
  date_to?: string;
  customer_id?: number;
  invoice_type?: "tax" | "simplified" | "credit_note";
  q?: string;
}

/** Защо фискална фактура при закриване не е възможна (BE `fiscal-availability`).
 *  `setting_off` — фирмата е в „Софтуерно“; `device_exclusive` — „И двете“ с
 *  апарат, който държи поредицата сам (X/MX); `other_device` — фактурите на
 *  фирмата са закачени за друго фискално устройство. */
export type FiscalInvoiceUnavailableReason =
  | "setting_off"
  | "no_company"
  | "no_printer"
  | "device_unsupported"
  | "device_exclusive"
  | "other_device"
  | "offline";

/**
 * Може ли закриването на сметка да издаде фактурата от фискалното устройство
 * (обща поредица на обекта) — за собственика на сесията, със същото
 * разрешаване на принтера като закриването. `message` е готовият текст на
 * отказа; `next_number` — следващият номер от общата поредица; `mode` —
 * „Къде се издават фактурите“ на фирмата; `software_allowed` — дали FE
 * предлага софтуерна фактура (false в `fiscal` — BE отказва всяка).
 */
export interface FiscalInvoiceAvailability {
  available: boolean;
  reason: FiscalInvoiceUnavailableReason | null;
  message: { bg: string; en: string } | null;
  printer_name: string | null;
  next_number: string | null;
  mode: InvoiceIssuingMode;
  software_allowed: boolean;
  /** Касата зад принтера кара картов терминал — фискална фактура с карта
   *  през него BE още отказва, затова „С карта“ и „Смесено“ отпадат от
   *  методите (`lib/fiscalInvoice.ts::fiscalInvoiceMethodsOf`). Липсва при
   *  по-старо BE — тогава всичко е както преди (отказът идва при „Закрий“). */
  card_terminal?: boolean;
}

// GET /invoices/fiscal-availability — право `invoicing`, само в облака.
export const getFiscalInvoiceAvailability = async (): Promise<FiscalInvoiceAvailability> => {
  const response = await api.get<FiscalInvoiceAvailability>("/invoices/fiscal-availability");
  return response.data;
};

export const listInvoices = async (
  filters?: InvoiceListFilters,
): Promise<Invoice[]> => {
  const params = new URLSearchParams();
  if (filters?.date_from) params.append("date_from", filters.date_from);
  if (filters?.date_to) params.append("date_to", filters.date_to);
  if (filters?.customer_id != null)
    params.append("customer_id", String(filters.customer_id));
  if (filters?.invoice_type)
    params.append("invoice_type", filters.invoice_type);
  if (filters?.q && filters.q.trim()) params.append("q", filters.q.trim());
  const qs = params.toString();
  const response = await api.get<Invoice[]>(
    qs ? `/invoices?${qs}` : "/invoices",
  );
  return response.data;
};

export const getInvoiceById = async (id: number): Promise<Invoice> => {
  const response = await api.get<Invoice>(`/invoices/${id}`);
  return response.data;
};

export const cancelInvoice = async (id: number): Promise<Invoice> => {
  const response = await api.post<Invoice>(`/invoices/${id}/cancel`);
  return response.data;
};

/**
 * Коригира получателя на издадена фактура — същите полета и валидации като
 * при издаване (BE `InvoiceCustomerIn`). Пипа само снимката върху самия
 * запис (`customer_*`), не регистъра на клиентите; суми, редове, номер и
 * дати не се променят. Анулирана фактура се отказва с 409.
 */
export const updateInvoiceCustomer = async (
  id: number,
  data: InvoiceCustomerPayload,
): Promise<Invoice> => {
  const response = await api.patch<Invoice>(`/invoices/${id}/customer`, data);
  return response.data;
};

export interface PrintInvoiceResult {
  status: "success" | string;
  result?: Record<string, unknown> | null;
}

/**
 * Печат на вече издадена фактура на POS (ESC/POS) принтера на оператора.
 *
 * `duplicate` — повторен печат от справките: излиза едно копие с етикет
 * „ДУБЛИКАТ", а не втори оригинал (оригиналът вече е у клиента). Успешният
 * печат на оригинала BE отбелязва сам (`original_printed_at`).
 */
export const printInvoicePos = async (
  id: number,
  options?: { duplicate?: boolean },
): Promise<PrintInvoiceResult> => {
  const qs = options?.duplicate ? "?duplicate=true" : "";
  const response = await api.post<PrintInvoiceResult>(
    `/invoices/${id}/print${qs}`,
  );
  return response.data;
};

/**
 * A4 печатът на ОРИГИНАЛА — печата браузърът, затова FE го съобщава на BE.
 * Връща фактурата с `original_printed_at` (първият печат; повторно — без промяна).
 */
export const markInvoiceOriginalPrinted = async (id: number): Promise<Invoice> => {
  const response = await api.post<Invoice>(`/invoices/${id}/original-printed`);
  return response.data;
};
